import React from 'react';
import { Drawer, Typography, Button, Descriptions } from 'antd';
import { useOwnerIntelligenceLocale } from '@/contexts/OwnerIntelligenceLocaleContext';

const { Paragraph } = Typography;

export function OiDetailDrawer({
  open,
  onClose,
  title,
  subtitle,
  children,
  actionLabel,
  onAction,
  width = 400,
}: {
  open: boolean;
  onClose: () => void;
  title: string;
  subtitle?: string;
  children: React.ReactNode;
  actionLabel?: string;
  onAction?: () => void;
  width?: number;
}) {
  const { dir } = useOwnerIntelligenceLocale();
  const placement = dir === 'rtl' ? 'left' : 'right';

  return (
    <Drawer
      open={open}
      onClose={onClose}
      title={title}
      placement={placement}
      width={width}
      extra={
        actionLabel && onAction ? (
          <Button type="primary" size="small" onClick={onAction}>
            {actionLabel}
          </Button>
        ) : null
      }
    >
      {subtitle ? <Paragraph type="secondary" style={{ marginBottom: 16 }}>{subtitle}</Paragraph> : null}
      {children}
    </Drawer>
  );
}

export function OiDetailJsonPanel({ data }: { data: Record<string, unknown> | unknown }) {
  if (!data || typeof data !== 'object') return <Paragraph>No detail available.</Paragraph>;
  const entries = Object.entries(data as Record<string, unknown>);
  if (!entries.length) return <Paragraph>No detail available.</Paragraph>;
  return (
    <Descriptions column={1} size="small" bordered>
      {entries.map(([k, v]) => (
        <Descriptions.Item key={k} label={k}>
          {typeof v === 'object' ? JSON.stringify(v) : String(v ?? '—')}
        </Descriptions.Item>
      ))}
    </Descriptions>
  );
}

export function ProportionalBar({
  segments,
}: {
  segments: Array<{ label: string; amount: number; color: string }>;
}) {
  const total = segments.reduce((s, x) => s + Math.max(0, x.amount), 0);
  if (total <= 0) return null;
  return (
    <div>
      <div className="oi-money-bar">
        {segments.map((seg) => (
          <div
            key={seg.label}
            className="oi-money-bar__segment"
            style={{ width: `${(seg.amount / total) * 100}%`, background: seg.color }}
            title={`${seg.label}: ${seg.amount}`}
          />
        ))}
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, marginTop: 8, fontSize: 12, color: 'var(--oi-text-muted)' }}>
        {segments.map((seg) => (
          <span key={seg.label}>
            <span style={{ display: 'inline-block', width: 8, height: 8, background: seg.color, marginRight: 4 }} />
            {seg.label}
          </span>
        ))}
      </div>
    </div>
  );
}
